import { useEffect, useState, useReducer, useRef } from 'react'
import { cloneDeep, sortBy, filter, findIndex } from 'lodash'
import { format, getDayOfYear } from 'date-fns'
import {
  formatDuration,
  getSpeedAndDistanceOptions,
} from '../assets/assets_functions.js'
import { useNavigate } from 'react-router-dom'

import {
  useListSessionsWS,
  useListBoards,
  useListSails,
  useLang,
  useUser,
  useRestricted,
} from '../utils/hooks'
import { langGlobalStats } from '../assets/langGlobalStats'

import StatList from '../components/StatList'
import PageSelect from '../components/PageSelect'

import MonthStatsChart from '../components/MonthStatsChart'
import StatsCalendar from '../components/StatsCalendar'
import {
  durationInMinutes,
  getMonthStatsChartOptions,
  getSpeedAndDistanceFromTrkpts,
  getTrkptsFromStr,
} from '../assets/assets_functions'
import {
  globalStatsNumberOfItemsPerPage,
  smallTextClass,
} from '../assets/colors'
import Footer from '../components/Footer.jsx'
import SpeedAndDistanceList from '../components/SpeedAndDistanceList.jsx'
import SpeedAndDistanceChart from '../components/SpeedAndDistanceChart.jsx'

const GlobalStats = () => {
  const SORTS = {
    NONE: (list) => list,
    YEAR_SORT: (list) => sortBy(list, 'year'),
    NB_SORT: (list) => sortBy(list, 'sessionsNb'),
    DURATION_SORT: (list) => sortBy(list, 'sessionsDuration'),
  }

  const SPEED_AND_DISTANCE_SORTS = {
    NONE: (list) => list,
    DATE_SORT: (list) => sortBy(list, 'date'),
    SPEED_SORT: (list) => sortBy(list, 'speed'),
    DISTANCE_SORT: (list) => sortBy(list, 'distance'),
    MAX_SPEED_RIG_SORT: (list) => sortBy(list, 'rig'),
  }

  const firstDiv = useRef(null)

  const { listSessionsWS } = useListSessionsWS()
  const { listBoards } = useListBoards()
  const { listSails } = useListSails()
  const { user } = useUser()
  const { restricted } = useRestricted()
  const { lang } = useLang()
  const [calStats, setCalStats] = useState([])
  const [nbStatsOptions, setNbStatsOptions] = useState(null)
  const [durationStatsOptions, setDurationStatsOptions] = useState(null)
  const [nbCumulStatsOptions, setNbCumulStatsOptions] = useState(null)
  const [durationCumulStatsOptions, setDurationCumulStatsOptions] =
    useState(null)
  const [cumulNbStats, setCumulNbStats] = useState(true)
  const [cumulDurationStats, setCumulDurationStats] = useState(true)

  const [speedAndDistanceStats, setSpeedAndDistanceStats] = useState({
    list: [],
    selectedItemId: '0',
  })
  const [filters, setFilters] = useState({
    sortOrder: { sortType: 'DATE_SORT', isReverse: true },
  })
  const [speedAndDistanceOptions, setSpeedAndDistanceOptions] = useState(null)
  const [speedOrDistanceType, setSpeedOrDistanceType] = useState('speed')

  const tempStats = cloneDeep(listSessionsWS)
  const choosenLang = lang === 'fr' ? langGlobalStats.fr : langGlobalStats.eng

  let navigateTo = useNavigate()

  const statsReducer = (state, action) => {
    switch (action.type) {
      case 'NEW_PAGE':
        return {
          ...state,
          listStatsPage: cloneDeep(
            state.data.slice(
              (action.payload - 1) * globalStatsNumberOfItemsPerPage,
              action.payload * globalStatsNumberOfItemsPerPage,
            ),
          ),
          selectedPage: action.payload,
        }
      case 'NEW_ITEM_SELECTED':
        return {
          ...state,
          data: cloneDeep(action.payload),
          listStatsPage: cloneDeep(
            action.payload.slice(
              (state.selectedPage - 1) * globalStatsNumberOfItemsPerPage,
              state.selectedPage * globalStatsNumberOfItemsPerPage,
            ),
          ),
          totalItems: action.payload.length,
        }
      case 'STATS_FETCH_OK':
        return {
          ...state,
          initialData: cloneDeep(action.payload),
          data: cloneDeep(action.payload),
          listStatsPage: cloneDeep(
            action.payload.slice(0, globalStatsNumberOfItemsPerPage),
          ),
          isLoading: false,
          isError: false,
          totalItems: action.payload.length,
          sortOrder: { sortType: 'YEAR_SORT', isReverse: true },
        }
      case 'STATS_FETCH_FAILED':
        return { ...state, isLoading: false, isError: true }
      case 'SORT_CHANGED':
        return {
          ...state,
          data: cloneDeep(action.payload.list),
          listStatsPage: cloneDeep(
            action.payload.list.slice(
              (state.selectedPage - 1) * globalStatsNumberOfItemsPerPage,
              state.selectedPage * globalStatsNumberOfItemsPerPage,
            ),
          ),
          sortOrder: action.payload.sort,
        }
      default:
        throw new Error()
    }
  }

  const [listStats, setListStats] = useReducer(statsReducer, {
    initialData: [],
    data: [],
    listStatsPage: [],
    isLoading: false,
    isError: false,
    selectedPage: 1,
    totalItems: 0,
    sortOrder: { sortType: 'NONE', isReverse: false },
    searchInput: '',
  })

  useEffect(() => {
    if (firstDiv.current !== null) {
      const topScroll = firstDiv.current.getBoundingClientRect().top
      window.scrollTo({ top: topScroll.current - 120, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (user.userId === -1) {
      if (listSessionsWS.data.length === 0) {
        const localUserId = localStorage.getItem('wsurf_userId')
        if (!localUserId || Number(localUserId) === -1) navigateTo('/login')
        else navigateTo('/sessions/')
      }
    }
  }, [])

  useEffect(() => {
    let years = []
    let stats = []
    let allCalSessions = []

    let currentYear = -1
    const tempList = sortBy(cloneDeep(tempStats.data), (session) =>
      new Date(session.sessionStart).getFullYear(),
    )
    tempList.forEach((session) => {
      const tempYear = new Date(session.sessionStart).getFullYear()
      if (tempYear !== currentYear) {
        currentYear = tempYear
        years.push(tempYear)
      }
    })

    years.forEach((year) => {
      let monthNb = []
      let monthDuration = []
      let monthCumulNb = []
      let monthCumulDuration = []
      let calSessions = []
      for (let i = 0; i < 12; i++) {
        monthNb[i] = 0
        monthDuration[i] = 0
      }
      const yearSessions = sortBy(
        filter(
          tempList,
          (session) => new Date(session.sessionStart).getFullYear() === year,
        ),
        (session) => new Date(session.sessionStart),
      )
      const sessionsTotalNb = yearSessions.length
      let sessionsTotalDuration = 0
      yearSessions.forEach((session) => {
        const myDate = new Date(session.sessionStart)
        const myDuration = session.restrictedSessionDuration * 1000
        const month = myDate.getMonth()
        monthNb[month]++
        monthDuration[month] += myDuration
        sessionsTotalDuration += myDuration
        const dayOfYear = getDayOfYear(myDate)
        const minutesDuration = durationInMinutes(myDuration)
        const dayExist = findIndex(
          calSessions,
          (session) => session.x === dayOfYear,
        )
        if (dayExist === -1) {
          calSessions.push({
            x: dayOfYear,
            y: year,
            z: minutesDuration,
            custom: {
              date: format(myDate, 'dd-MM-yy'),
              duree: formatDuration(myDuration),
              n: 1,
              nb: '1 session',
              type_sport: 'Windsurf',
            },
            color: '#87CEEB',
          })
        } else {
          const tempN = calSessions[dayExist].custom.n + 1
          const tempDuration = calSessions[dayExist].z + minutesDuration
          calSessions[dayExist].custom.n = tempN
          calSessions[dayExist].custom.nb = tempN + ' sessions'
          calSessions[dayExist].z = tempDuration
          calSessions[dayExist].custom.duree = formatDuration(
            tempDuration * 60000,
          )
        }
      })
      let tempNb = 0
      let tempDuration = 0
      monthNb.forEach((nb) => {
        monthCumulNb.push(nb + tempNb)
        tempNb = monthCumulNb[monthCumulNb.length - 1]
      })
      monthDuration.forEach((duration) => {
        monthCumulDuration.push(duration + tempDuration)
        tempDuration = monthCumulDuration[monthCumulDuration.length - 1]
      })
      stats.push({
        year: year,
        sessionsNb: sessionsTotalNb,
        sessionsDuration: sessionsTotalDuration,
        monthNb: monthNb,
        monthDuration: monthDuration,
        monthCumulNb: monthCumulNb,
        monthCumulDuration: monthCumulDuration,
        bg: '',
      })
      allCalSessions.push({ data: calSessions, name: year })
    })
    stats = sortBy(stats, 'year').reverse()

    setListStats({ type: 'STATS_FETCH_OK', payload: stats })
    setCalStats(allCalSessions)
  }, [listSessionsWS])

  useEffect(() => {
    if (listStats.initialData.length === 0) return
    const temp_nbStatsOptions = getMonthStatsChartOptions(
      listStats.initialData,
      'nb',
      false,
      choosenLang,
    )
    const temp_nbCumulStatsOptions = getMonthStatsChartOptions(
      listStats.initialData,
      'nb',
      true,
      choosenLang,
    )
    const temp_durationStatsOptions = getMonthStatsChartOptions(
      listStats.initialData,
      'duration',
      false,
      choosenLang,
    )
    const temp_durationCumulStatsOptions = getMonthStatsChartOptions(
      listStats.initialData,
      'duration',
      true,
      choosenLang,
    )
    setNbStatsOptions(temp_nbStatsOptions)
    setNbCumulStatsOptions(temp_nbCumulStatsOptions)
    setDurationStatsOptions(temp_durationStatsOptions)
    setDurationCumulStatsOptions(temp_durationCumulStatsOptions)
  }, [listStats, choosenLang])

  useEffect(() => {
    if (listSessionsWS.length === 0) return
    if (speedAndDistanceStats.list.length !== 0) return

    const tempSessions = listSessionsWS.initialData.map((session) => {
      let board = ''
      let sail = ''
      console.log(session)
      if (session.maxSpeedBoardId) {
        const tempBoardIndex = findIndex(
          listBoards.data,
          (board) => board._id === session.maxSpeedBoardId,
        )
        if (tempBoardIndex !== -1) {
          const tempBoard = listBoards.data[tempBoardIndex]
          board =
            tempBoard.brand + ' ' + tempBoard.model + ' ' + tempBoard.volume
        }
      }
      if (session.maxSpeedSailId) {
        const tempSailIndex = findIndex(
          listSails.data,
          (sail) => sail._id === session.maxSpeedSailId,
        )
        if (tempSailIndex !== -1) {
          const tempSail =
            listSails.data[
              findIndex(
                listSails.data,
                (sail) => sail._id === session.maxSpeedSailId,
              )
            ]
          sail = tempSail.brand + ' ' + tempSail.model + ' ' + tempSail.surface
        }
      }

      const maxSpeedRig =
        board === '' && sail === ''
          ? 'no rigs for the session'
          : board + ' / ' + sail
      return {
        speed: session.maxSpeed,
        distance: session.distance,
        date: new Date(session.sessionStart),
        id: session._id,
        rig: maxSpeedRig,
      }
    })
    setSpeedAndDistanceStats({
      list: tempSessions,
      selectedItemId: '0',
    })

    console.log('ok')
  }, [
    listSessionsWS,
    speedAndDistanceStats,
    restricted,
    choosenLang,
    speedOrDistanceType,
    listBoards,
    listSails,
  ])

  useEffect(() => {
    if (speedAndDistanceStats.list.length === 0) return
    const tempOptions = getSpeedAndDistanceOptions(
      sortBy(speedAndDistanceStats.list, ['date']).reverse(),
      speedOrDistanceType,
      choosenLang,
    )
    setSpeedAndDistanceOptions(tempOptions)
  }, [speedAndDistanceStats, speedOrDistanceType, choosenLang])

  const handleSelectNewItem = (item) => {
    const temp = cloneDeep(listStats.data)
    const old_index = temp
      .map(function (e) {
        return e.bg
      })
      .indexOf('selected')
    const new_index = temp
      .map(function (e) {
        return e.year
      })
      .indexOf(item.year)

    if (old_index !== -1) {
      temp[old_index].bg = ''
    }

    if (old_index !== new_index) {
      temp[new_index].bg = 'selected'
    }
    setListStats({ type: 'NEW_ITEM_SELECTED', payload: temp })
  }
  const handleSortStats = (column_type) => {
    const sort_order =
      column_type === listStats.sortOrder.sortType
        ? !listStats.sortOrder.isReverse
        : true
    let sort_type = column_type
    const sortFunction = SORTS[sort_type]
    const temp = sort_order
      ? sortFunction(Array.from(listStats.data)).reverse()
      : sortFunction(Array.from(listStats.data))
    setListStats({
      type: 'SORT_CHANGED',
      payload: {
        list: temp,
        sort: { sortType: column_type, isReverse: sort_order },
      },
    })
  }

  const handleSortSpeedAndDistance = (column_type) => {
    const sort_order =
      column_type === filters.sortOrder.sortType
        ? !filters.sortOrder.isReverse
        : true
    let sort_type = column_type
    const sortFunction = SPEED_AND_DISTANCE_SORTS[sort_type]
    const temp = sort_order
      ? sortFunction(Array.from(speedAndDistanceStats.list)).reverse()
      : sortFunction(Array.from(speedAndDistanceStats.list))
    setSpeedAndDistanceStats({ ...speedAndDistanceStats, list: temp })
    setFilters({ sortOrder: { sortType: column_type, isReverse: sort_order } })
  }

  const handleClickSelectPage = (num_page) => {
    setListStats({ type: 'NEW_PAGE', payload: parseInt(num_page) })
  }

  const handleNbCumul = () => {
    setCumulNbStats(!cumulNbStats)
  }

  const handleDurationCumul = () => {
    setCumulDurationStats(!cumulDurationStats)
  }

  const handleSelectNewSpeedAndDistanceItem = (item) => {
    if (item) {
      setSpeedAndDistanceStats({
        ...speedAndDistanceStats,
        selectedItemId:
          item.id !== speedAndDistanceStats.selectedItemId ? item.id : '0',
      })
    }
  }

  const handleSpeedOrDistanceTypeChange = () => {
    setSpeedOrDistanceType(
      speedOrDistanceType === 'speed' ? 'distance' : 'speed',
    )
  }

  return (
    <>
      <section className="section" ref={firstDiv}>
        <h1 className="title is-4 has-text-info-dark has-text-weight-semibold">
          {choosenLang.title}
        </h1>
        {listStats.initialData.length > 0 ? (
          <div className="columns is-variable is-8">
            <div className="column is-4">
              <div className="block">
                <h1 className="title is-6 has-text-weight-semibold">
                  {choosenLang.tabTitle}
                </h1>
                <StatList
                  list={listStats}
                  handleSelectNewItem={handleSelectNewItem}
                  handleSort={handleSortStats}
                  textClass={smallTextClass}
                />
                <PageSelect
                  handleClickSelectPage={handleClickSelectPage}
                  list={listStats}
                  itemsPerPage={globalStatsNumberOfItemsPerPage}
                  buttonsActive={true}
                />
              </div>
            </div>
            <div className="column is-4">
              {cumulNbStats === false && (
                <MonthStatsChart
                  title={choosenLang.nbTitle + choosenLang.nbDetailTitle}
                  options={nbStatsOptions}
                  checked={false}
                  handleCumulClick={handleNbCumul}
                />
              )}
              {cumulNbStats === true && (
                <MonthStatsChart
                  title={choosenLang.nbTitle + choosenLang.nbCumulTitle}
                  options={nbCumulStatsOptions}
                  checked={true}
                  handleCumulClick={handleNbCumul}
                />
              )}
            </div>
            <div className="column is-4">
              {cumulDurationStats === false && (
                <MonthStatsChart
                  title={
                    choosenLang.durationTitle + choosenLang.durationDetailTitle
                  }
                  options={durationStatsOptions}
                  checked={false}
                  handleCumulClick={handleDurationCumul}
                />
              )}
              {cumulDurationStats === true && (
                <MonthStatsChart
                  title={
                    choosenLang.durationTitle + choosenLang.durationCumulTitle
                  }
                  options={durationCumulStatsOptions}
                  checked={true}
                  handleCumulClick={handleDurationCumul}
                />
              )}
            </div>
          </div>
        ) : (
          listStats.isLoading === false && (
            <div className="notification is-warning is-light is-medium">
              {choosenLang.noStats}
            </div>
          )
        )}
      </section>
      {listStats.initialData.length > 0 && (
        <section className="section">
          <div className="box">
            {calStats.length > 0 && <StatsCalendar data={calStats} />}
          </div>
        </section>
      )}
      <section>
        {speedAndDistanceStats.list.length > 0 && (
          <div className="box">
            <h1 className="title is-6  has-text-weight-semibold">
              {choosenLang.maxSpeedDistanceTitle}
            </h1>
            <div className="columns">
              <div className="column is-6">
                <SpeedAndDistanceList
                  data={speedAndDistanceStats}
                  filters={filters}
                  handleSelectNewItem={handleSelectNewSpeedAndDistanceItem}
                  handleSort={handleSortSpeedAndDistance}
                  textClass={smallTextClass}
                />
              </div>
              <div className="column">
                {speedAndDistanceOptions && (
                  <SpeedAndDistanceChart
                    options={speedAndDistanceOptions}
                    radioLabels={[choosenLang.maxSpeed, choosenLang.distance]}
                    type={speedOrDistanceType}
                    handleTypeChange={handleSpeedOrDistanceTypeChange}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </section>
      <Footer />
    </>
  )
}

export default GlobalStats
